import { useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { MAX_BANNER_BYTES, US_STATES } from "@alpha/shared";
import { api } from "../../../lib/api";
import { useAdminMutation, useAdminVenues } from "../../../lib/adminData";
import { bannerUploadEndpoint } from "../../../lib/imageUpload";
import type { Caps } from "../../../lib/tournamentPhases";
import type { Bundle, Venue } from "../../../lib/types";
import { ImageUploadField } from "../../../components/ImageUploadField";
import {
  Badge,
  Button,
  ButtonLink,
  cx,
  Field,
  Input,
  SectionCard,
  Select,
  Textarea,
} from "../../../components/ui";
import { Divisions } from "./Divisions";
import { Rules } from "./Rules";
import { Staff } from "./Staff";
import type { TournamentContext } from "./TournamentShell";

/**
 * The event itself (console spec §5.2): details, the registration window and
 * the rules for anyone who may edit the record; divisions and pools, staff,
 * and the delete for whoever runs the event.
 */
export function Setup() {
  const { tid, bundle, caps, pendingRegistrations } = useOutletContext<TournamentContext>();
  return (
    <div className="space-y-6">
      <EventDetails tid={tid} bundle={bundle} caps={caps} />
      <RegistrationWindow tid={tid} bundle={bundle} />
      <Rules tid={tid} bundle={bundle} />
      {caps.runEvent ? (
        <Divisions tid={tid} bundle={bundle} pendingRegistrations={pendingRegistrations} />
      ) : null}
      {caps.runEvent ? <Staff tid={tid} /> : null}
      {caps.runEvent ? <DeleteTournament tid={tid} name={bundle.tournament.name} /> : null}
    </div>
  );
}

/**
 * The event as teams see it: its name and dates, the blurb, the city, and which
 * venues from the library it plays at — all shown on the public tournament
 * page. Without this editor a published tournament has no
 * description and no venues, so those sections never render for a coach.
 *
 * State is the exception: it does not appear on the tournament page at all, and
 * exists so the event can be found by location on /tournaments.
 */
function EventDetails({ tid, bundle, caps }: { tid: string; bundle: Bundle; caps: Caps }) {
  const t = bundle.tournament;
  const qc = useQueryClient();
  const venuesQ = useAdminVenues();
  const venues = venuesQ.data?.venues ?? [];

  const [name, setName] = useState(t.name);
  const [startDate, setStartDate] = useState(t.startDate);
  const [endDate, setEndDate] = useState(t.endDate);
  const [description, setDescription] = useState(t.description ?? "");
  const [location, setLocation] = useState(t.location ?? "");
  const [state, setState] = useState(t.state ?? "");
  const [venueIds, setVenueIds] = useState<string[]>(t.venueIds ?? []);
  const [bannerImageUrl, setBannerImageUrl] = useState(t.bannerImageUrl ?? "");

  const [venueName, setVenueName] = useState("");
  const [venueAddress, setVenueAddress] = useState("");
  const [fieldName, setFieldName] = useState("");

  const save = useAdminMutation(tid, () =>
    api.put(`/admin/tournaments/${tid}`, {
      name: name.trim(),
      startDate,
      endDate,
      description,
      location,
      state,
      venueIds,
      bannerImageUrl: bannerImageUrl.trim() || null,
    }),
  );

  const datesInvalid = Boolean(startDate && endDate && endDate < startDate);
  const invalid = !name.trim() || !startDate || !endDate || datesInvalid;

  const toggleVenue = (id: string) =>
    setVenueIds((cur) => (cur.includes(id) ? cur.filter((v) => v !== id) : [...cur, id]));

  const addVenue = useAdminMutation(tid, () =>
    api.post<Venue>(`/admin/tournaments/${tid}/venues`, {
      name: venueName.trim(),
      address: venueAddress.trim(),
      fields: [{ name: fieldName.trim() }],
    }),
  );
  const venueInvalid = !venueName.trim() || !venueAddress.trim() || !fieldName.trim();

  return (
    <SectionCard
      title="Event details"
      description="Shown on the public tournament page."
      actions={
        // The venue library is the whole organization's, not this event's, so
        // only a global admin may edit it — and only a global admin is let into
        // /admin/venues at all. A tournament admin still picks from the chips
        // below, which is exactly the read their grant covers.
        caps.manage ? (
          <ButtonLink variant="secondary" size="sm" to="/admin/venues">
            Manage venues
          </ButtonLink>
        ) : undefined
      }
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!invalid) save.mutate();
        }}
      >
        {/*
          The card runs the full shell width for the venue chips and the banner
          preview, but prose does not get more readable at 1400px and a city
          name does not need a 700px box. The two text inputs on this form keep
          their own ceiling.
        */}
        {/* The name and dates lead: the day strip on Scores is built from the
            dates, so a wrong date has to be fixable here. */}
        <div className="grid max-w-2xl gap-4 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)]">
          <Field label="Name">
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={160} required />
          </Field>
          <Field label="Start date">
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} required />
          </Field>
          <Field label="End date">
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} required />
          </Field>
        </div>
        {datesInvalid ? <p className="text-sm text-red-600">The end date is before the start date.</p> : null}
        <Field
          label="Description"
          className="max-w-4xl"
          hint="What teams should know before they enter — format, age rules, awards."
        >
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={2000}
            placeholder="Three-game guarantee, wood bat only, championship Sunday at noon…"
          />
        </Field>
        <div className="grid max-w-2xl gap-4 sm:grid-cols-2">
          <Field label="City" hint="Appears under the dates. Example: Dallas, TX">
            <Input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              maxLength={200}
              placeholder="Dallas, TX"
            />
          </Field>
          <Field label="State" hint="Powers the location filter on the tournaments page.">
            <Select value={state} onChange={(e) => setState(e.target.value)}>
              <option value="">Not set</option>
              {US_STATES.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <ImageUploadField
          label="Banner image"
          endpoint={bannerUploadEndpoint(tid)}
          maxBytes={MAX_BANNER_BYTES}
          value={bannerImageUrl}
          onChange={setBannerImageUrl}
          shape="wide"
          saveHint="Save details to apply it."
          linkPlaceholder="https://example.com/banner.jpg"
        />
        <div>
          <span className="mb-1.5 block text-sm font-medium text-slate-600">Venues</span>
          {venuesQ.isLoading ? (
            <p className="text-sm text-slate-400">Loading venues…</p>
          ) : venues.length === 0 ? (
            <p className="text-sm text-slate-400">
              {caps.manage
                ? "No venues in the library yet. Add one under Manage venues, then attach it here."
                : caps.editEvent
                  ? "No venues in the library yet. Add one below, then it is attached to this event."
                  : "No venues in the library yet. Ask an organizer to add one, then attach it here."}
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {venues.map((v) => {
                const on = venueIds.includes(v.id);
                return (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => toggleVenue(v.id)}
                    aria-pressed={on}
                    className={cx(
                      "rounded-lg border px-3 py-1.5 text-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600",
                      on
                        ? "border-brand-600 bg-brand-50 font-medium text-brandText"
                        : "border-slate-300 bg-white text-slate-600 hover:border-slate-400",
                    )}
                  >
                    {v.name}
                  </button>
                );
              })}
            </div>
          )}
          {caps.editEvent && !caps.manage ? (
            <details className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
              <summary className="cursor-pointer font-medium text-slate-700">Add a venue</summary>
              <div className="mt-4 grid gap-4 sm:grid-cols-3">
                <Field label="Name">
                  <Input value={venueName} onChange={(e) => setVenueName(e.target.value)} />
                </Field>
                <Field label="Address">
                  <Input value={venueAddress} onChange={(e) => setVenueAddress(e.target.value)} />
                </Field>
                <Field label="Field name">
                  <Input value={fieldName} onChange={(e) => setFieldName(e.target.value)} />
                </Field>
                <div className="sm:col-span-3">
                  <Button
                    type="button"
                    disabled={venueInvalid || addVenue.isPending}
                    onClick={() => {
                      if (!venueInvalid) {
                        addVenue.mutate(undefined, {
                          onSuccess: (venue) => {
                            // The server has attached it, but venueIds is seeded
                            // once, not from the refetched bundle. Without this,
                            // Save details would write the list back without it.
                            setVenueIds((cur) => (cur.includes(venue.id) ? cur : [...cur, venue.id]));
                            void qc.invalidateQueries({ queryKey: ["admin-venues"] });
                            void qc.invalidateQueries({ queryKey: ["admin-bundle", tid] });
                            setVenueName("");
                            setVenueAddress("");
                            setFieldName("");
                          },
                        });
                      }
                    }}
                  >
                    {addVenue.isPending ? "Adding…" : "Add venue"}
                  </Button>
                </div>
              </div>
            </details>
          ) : null}
        </div>
        <Button type="submit" disabled={invalid || save.isPending}>
          {save.isPending ? "Saving…" : "Save details"}
        </Button>
      </form>
    </SectionCard>
  );
}


function toLocalInput(iso: string | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Registration window (spec §8.3). Publishing a tournament does NOT open
 * registration: teams only see it in the register-a-team dropdown while this
 * window is open, and the API rejects registrations outside it. Without this
 * editor a published tournament could never accept a registration.
 */
function RegistrationWindow({ tid, bundle }: { tid: string; bundle: Bundle }) {
  const reg = bundle.tournament.registration;
  const [opensAt, setOpensAt] = useState(() => toLocalInput(reg?.opensAt));
  const [closesAt, setClosesAt] = useState(() => toLocalInput(reg?.closesAt));

  const save = useAdminMutation(tid, () =>
    api.put(`/admin/tournaments/${tid}`, {
      registration: {
        opensAt: new Date(opensAt).toISOString(),
        closesAt: new Date(closesAt).toISOString(),
      },
    }),
  );
  const clear = useAdminMutation(tid, () =>
    api.put(`/admin/tournaments/${tid}`, { registration: null }),
  );

  const now = Date.now();
  const isOpen = !!reg && Date.parse(reg.opensAt) <= now && now <= Date.parse(reg.closesAt);
  const invalid = !opensAt || !closesAt || Date.parse(closesAt) <= Date.parse(opensAt);

  return (
    <SectionCard
      title="Registration window"
      badge={
        reg ? (
          isOpen ? (
            <Badge tone="green" pulse>
              Open now
            </Badge>
          ) : (
            <Badge tone="slate">Not open</Badge>
          )
        ) : undefined
      }
      description={
        reg
          ? `${new Date(reg.opensAt).toLocaleString()} – ${new Date(reg.closesAt).toLocaleString()}`
          : "Not set — teams cannot register until you set a window, even though the tournament is published."
      }
    >
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!invalid) save.mutate();
        }}
      >
        <Field label="Opens (your local time)" className="w-full sm:w-auto">
          <Input
            type="datetime-local"
            value={opensAt}
            onChange={(e) => setOpensAt(e.target.value)}
            required
          />
        </Field>
        <Field label="Closes (your local time)" className="w-full sm:w-auto">
          <Input
            type="datetime-local"
            value={closesAt}
            onChange={(e) => setClosesAt(e.target.value)}
            required
          />
        </Field>
        <div className="flex gap-2">
          <Button type="submit" disabled={invalid || save.isPending}>
            {save.isPending ? "Saving…" : "Save window"}
          </Button>
          {reg ? (
            <Button type="button" variant="danger" onClick={() => clear.mutate()} disabled={clear.isPending}>
              Close registration
            </Button>
          ) : null}
        </div>
      </form>
      {invalid && opensAt && closesAt ? (
        <p className="text-sm text-red-600">Closes must be after opens.</p>
      ) : null}
    </SectionCard>
  );
}

/**
 * The way out, at the foot of Setup rather than beside Publish in the header:
 * a destructive control next to the one an organizer presses every event was
 * a stray click waiting to happen. It enables only once the tournament's name
 * has been typed beside it — the confirmation is the name, not a second tap.
 */
function DeleteTournament({ tid, name }: { tid: string; name: string }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [typed, setTyped] = useState("");
  const destroy = useAdminMutation(tid, () => api.del(`/admin/tournaments/${tid}`));
  const matches = typed.trim() === name.trim();

  return (
    <SectionCard
      title="Delete tournament"
      description="Removes the tournament, its games, and every registration. This cannot be undone."
    >
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!matches) return;
          destroy.mutate(undefined, {
            onSuccess: () => {
              void qc.invalidateQueries({ queryKey: ["admin-tournaments"] });
              navigate("/admin");
            },
          });
        }}
      >
        <Field label="Type the tournament's name to confirm" className="w-full sm:w-80">
          <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={name} />
        </Field>
        <Button type="submit" variant="danger" disabled={!matches || destroy.isPending}>
          {destroy.isPending ? "Deleting…" : "Delete tournament"}
        </Button>
      </form>
    </SectionCard>
  );
}
